/**
 * O estado de "estou apontando uma pendência" — um só, para a tela inteira.
 *
 * **Por que o estado é da página e não do cartão da trilha.** A pendência pode nascer de dois
 * lugares: do cartão da trilha ("esta trilha precisa voltar") ou da própria divergência ("estes
 * dois valores não batem — devolva o primeiro"). Se cada cartão guardasse o seu, clicar na
 * divergência teria de abrir um segundo formulário em outro canto, e dois formulários abertos ao
 * mesmo tempo são duas descrições concorrendo para virar a mesma pendência. Um alvo só, com
 * trilha e pergunta, resolve os dois caminhos com um formulário.
 *
 * Vive num módulo `.ts` separado dos componentes de propósito: `react-refresh` avisa quando um
 * arquivo de componente também exporta outra coisa, e o aviso está certo — misturar as duas
 * naturezas quebra a atualização quente durante o desenvolvimento.
 */

import { useCallback, useState } from "react";
import { mensagemDoConsole, useApontarPendencia } from "../../nucleo/console";

export interface AlvoDaPendencia {
  trilha: string;
  /** A pergunta exata, quando a pendência é sobre uma. `null` aponta a trilha inteira. */
  perguntaId: string | null;
}

/* O mesmo mínimo que o servidor cobra (`TAMANHO_MINIMO_DA_PENDENCIA`), repetido aqui para a tela
   não deixar enviar o que vai voltar `422`. Quem recusa continua sendo ele. O motivo do mínimo é
   concreto: a descrição é lida pelo CLIENTE na esteira dele (`esteira._pendencias` lê
   `detalhe.descricao`), e "ok" ou "." no lugar do texto vira um "Sua vez" que não diz o que
   fazer. */
export const MINIMO_DA_PENDENCIA = 10;
export const MAXIMO_DA_PENDENCIA = 500;

/** Se este texto pode virar pendência. Apara antes de medir, como o servidor faz. */
export function pendenciaEstaEscrita(descricao: string): boolean {
  return descricao.trim().length >= MINIMO_DA_PENDENCIA;
}

export function useAcaoDaPendencia(implantacaoId: string) {
  const [alvo, setAlvo] = useState<AlvoDaPendencia | null>(null);
  const pendencia = useApontarPendencia(implantacaoId);

  const abrir = useCallback(
    (trilha: string, perguntaId: string | null = null) => {
      pendencia.reset();
      setAlvo({ trilha, perguntaId });
    },
    [pendencia],
  );

  const fechar = useCallback(() => setAlvo(null), []);

  const apontar = useCallback(
    (descricao: string) => {
      if (!alvo || !pendenciaEstaEscrita(descricao)) return;
      pendencia.mutate(
        { trilha: alvo.trilha, descricao: descricao.trim(), pergunta_id: alvo.perguntaId },
        // Fecha só no sucesso: com a recusa na tela, o texto escrito continua no campo em vez de
        // obrigar a pessoa a redigitá-lo.
        { onSuccess: () => setAlvo(null) },
      );
    },
    [alvo, pendencia],
  );

  return {
    alvo,
    abrir,
    fechar,
    apontar,
    enviando: pendencia.isPending,
    erro: pendencia.isError ? mensagemDoConsole(pendencia.error) : null,
    apontada: pendencia.data ?? null,
  };
}

export type AcaoDaPendencia = ReturnType<typeof useAcaoDaPendencia>;
